import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import excelIcon from "../../../../../icons/excel.png";
import myobIcon from "../../../../../icons/myob.jpeg";
import qbIcon from "../../../../../icons/qb-logo-horizontal-preferred.svg";
import quickenIcon from "../../../../../icons/quicken.png";
import netsuiteIcon from "../../../../../icons/netsuite.jpeg";

import relateIcon from "../../../../../icons/relate.png";
import sageIcon from "../../../../../icons/sage.png";
import xeroIcon from "../../../../../icons/xero.png";
import ofxIcon from "../../../../../icons/ofx.jpeg";

import { ReduxState } from "../../../../../redux";
import {
  setSelectedExportType,
  setSelectedCustomConfigCSV,
} from "../../../../../redux/reducers/DownloadOptions";
import { useLocalStorage } from "../../../../../utils/prefs";
import { IconOption, IconValue } from "./utils";
import ClipLoader from "react-spinners/ClipLoader";
import {
  fetchCustomFormats,
  deleteSelectedCustomFormat,
  setSelectedCustomFormat,
  updateSelected,
  createCustomFormatRedux,
} from "../../../../../redux/reducers/CustomFormat";
import { useHasQbo } from "../../../../../utils/utils";
import { ExportType } from "src/docuclipper/DocuclipperTypes";
import { toggleModal as toggleColumnConfigModal } from "../../../../../redux/reducers/ColumnConfig";

const invoiceOptions = [
  {
    value: "csv",
    label: "Excel (CSV)",
    icon: excelIcon,
  },
  {
    value: "xlsx",
    label: "Excel (XLSX)",
    icon: excelIcon,
  },
  {
    value: "qbo",
    label: "QuickBooks Online",
    icon: qbIcon,
  },
];

const allOptions = [
  {
    value: "csv",
    label: "Excel",
    icon: excelIcon,
  },
  {
    value: "qbo",
    label: "QuickBooks (Web Connect)",
    icon: qbIcon,
    iconHeight: "36px",
  },
  {
    value: "ofx",
    label: "OFX",
    icon: ofxIcon,
  },
  {
    value: "qfx",
    label: "QFX",
    icon: quickenIcon,
  },
  {
    value: "xero-csv",
    label: "Xero CSV",
    icon: xeroIcon,
  },
  {
    value: "xero-sync",
    label: "Xero Direct Sync",
    icon: xeroIcon,
  },
  {
    value: "sage-cloud-csv",
    label: "Sage Cloud CSV",
    icon: sageIcon,
  },
  {
    value: "sage-csv",
    label: "Sage CSV",
    icon: sageIcon,
  },
  {
    value: "myob",
    label: "MYOB CSV",
    icon: myobIcon,
  },
  {
    value: "relate",
    label: "Relate CSV",
    icon: relateIcon,
  },
  {
    value: "iif",
    label: "QuickBooks Desktop IIF",
    icon: qbIcon,
  },
  {
    value: "qif",
    label: "Quicken QIF",
    icon: quickenIcon,
  },
  {
    value: "netsuite",
    label: "NetSuite",
    icon: netsuiteIcon,
  },
];

const defaultSecondSelectOptions = [
  {
    value: "custom-all",
    label: "All columns",
  },
  {
    value: "custom-basic",
    label: "Basic — date, description, amount",
  },
];

const addNewCustomOption = {
  value: "add-new",
  label: "+ Create New Export Format",
};

export const ExportTypeDropdown: React.FC<{ jobType }> = ({ jobType }) => {
  const dispatch = useDispatch();

  const onSelectedOption = (selectedOption: any) => {
    dispatch(
      setSelectedCustomFormat(selectedOption.value.replace("custom-", ""))
    );
    dispatch(setSelectedExportType(selectedOption.value as ExportType));
    dispatch(setSelectedCustomConfigCSV(selectedOption));
  };

  const { customFormats, customFormatType, loading, selected } = useSelector(
    (state: ReduxState) => state.CustomFormat
  );
  const { selectedExportType, selectedCustomConfigCSV } = useSelector(
    (state: ReduxState) => state.DownloadOptions
  );

  // console.log({ customFormats, selectedExportType });

  // Only trust customFormats if the shared slice was last populated for "bankStatement".
  // The slice is shared with the invoice flow; without this guard a concurrent invoice
  // fetch can populate the dropdown with invoice formats that the bank backend doesn't
  // recognize (FieldsToExcel1.ts throws INVALID_OUTPUT_FORMAT / errorCode 1076).
  const bankFormats = customFormatType === "bankStatement" ? customFormats : [];
  const customOptions = bankFormats
    .map((x) => ({
      value: `custom-${x.name}`,
      label: x.name.charAt(0).toUpperCase() + x.name.slice(1),
      onEdit: () => {
        dispatch(setSelectedCustomFormat(`${x.name}`));
        dispatch(setSelectedExportType(`custom-${x.name}` as any));
        dispatch(toggleColumnConfigModal(true));
      },
      onDelete: () => {
        if (
          window.confirm("Are you sure you want to delete this configuration?")
        ) {
          const deletedFormatName = x.name;
          const isCurrentlySelected = selectedCustomConfigCSV.value === `custom-${deletedFormatName}`;
          
          dispatch(setSelectedCustomFormat(`${x.name}`));
          dispatch(setSelectedExportType(`custom-${x.name}` as any));
          dispatch(deleteSelectedCustomFormat());

          if (isCurrentlySelected) {
            // Switch to "all columns" (custom-all) after deletion
            const allColumnsOption = defaultSecondSelectOptions.find(
              (opt) => opt.value === "custom-all"
            );
            if (allColumnsOption) {
              onSelectedOption(allColumnsOption);
            }
          }
        }
      },
    }))
    .filter((x) => x.value !== "custom-all" && x.value !== "custom-basic");

  const secondSelectOptions = [
    ...defaultSecondSelectOptions,
    {
      value: "custom",
      label: "Custom",
      options: [addNewCustomOption].concat(customOptions),
    },
  ];

  const getOptions = () => {
    if (jobType === "Invoice") {
      return invoiceOptions;
    } else if (jobType === "Generic" /* || !hasQbo*/) {
      return allOptions.filter((o) => o.value === "xlsx");
    } else {
      return allOptions;
    }
  };

  const optionsByType = getOptions();

  // console.log({ optionsByType, jobType, allOptions, hasQbo });
  useEffect(() => {
    dispatch(
      fetchCustomFormats({
        init: false,
        type: "bankStatement",
      })
    );

    const defaultFirstSelectOption =
      optionsByType.find((option) => option?.value === selectedExportType) ||
      allOptions[0];

    if (defaultFirstSelectOption.value === "csv") {
      dispatch(
        setSelectedCustomFormat(
          selectedCustomConfigCSV.value.replace("custom-", "")
        )
      );
      dispatch(
        setSelectedExportType(selectedCustomConfigCSV.value as ExportType)
      );
      dispatch(setSelectedCustomConfigCSV(selectedCustomConfigCSV));
    } else {
      dispatch(updateSelected(null));
      dispatch(
        setSelectedExportType(defaultFirstSelectOption.value as ExportType)
      );
    }
  }, []);

  // Re-fetch if the shared CustomFormat slice is holding invoice formats (the invoice
  // modal's concurrent fetch won and the bank fetch was dropped by the early-return
  // guard in fetchCustomFormats). Without this, the self-heal below bails on
  // `customFormatType !== "bankStatement"` and stale custom-* values survive.
  useEffect(() => {
    if (loading) return;
    if (customFormatType === "bankStatement") return;
    dispatch(fetchCustomFormats({ init: false, type: "bankStatement" }));
  }, [loading, customFormatType, dispatch]);

  // Self-heal stale custom-* values in selectedExportType / selectedCustomConfigCSV. Both
  // persist in localStorage and can carry a custom-<invoiceFormatName> across sessions
  // after the invoice flow wrote to the shared CustomFormat slice. If the stored value
  // does not correspond to a bankStatement format in the currently-loaded list, fall
  // back to "custom-all" so the submit matches what the dropdown visually shows —
  // otherwise the backend rejects the export with "Invalid output format" (errorCode
  // 1076, FieldsToExcel1.ts INVALID_OUTPUT_FORMAT).
  useEffect(() => {
    if (loading) return;
    if (customFormatType !== "bankStatement") return;
    const isStaleCustom = (value: string | undefined) =>
      typeof value === "string" &&
      value.startsWith("custom-") &&
      value !== "custom-all" &&
      value !== "custom-basic" &&
      !customFormats.some((cf) => `custom-${cf.name}` === value);
    const exportTypeStale = isStaleCustom(selectedExportType);
    const configCsvStale = isStaleCustom(selectedCustomConfigCSV?.value);
    if (exportTypeStale || configCsvStale) {
      if (exportTypeStale) {
        dispatch(setSelectedExportType("custom-all" as any));
      }
      if (configCsvStale) {
        dispatch(
          setSelectedCustomConfigCSV({
            value: "custom-all",
            label: "All (all columns)",
          })
        );
      }
    }
  }, [
    loading,
    customFormatType,
    customFormats,
    selectedExportType,
    selectedCustomConfigCSV,
    dispatch,
  ]);

  if (loading) {
    return <ClipLoader />;
  }

  return (
    <>
      <Select
        maxMenuHeight={600}
        className="select-export-type"
        options={optionsByType}
        value={optionsByType.find(
          (option) =>
            option.value ===
            (selectedExportType?.startsWith("custom-")
              ? "csv"
              : selectedExportType)
        )}
        onChange={(selectedOption) => {
          if (selectedOption) {
            if (selectedOption.value === "csv") {
              dispatch(
                setSelectedCustomFormat(
                  selectedCustomConfigCSV.value.replace("custom-", "")
                )
              );
              dispatch(
                setSelectedExportType(
                  selectedCustomConfigCSV.value as ExportType
                )
              );
              dispatch(setSelectedCustomConfigCSV(selectedCustomConfigCSV));
            } else {
              dispatch(updateSelected(null));
              dispatch(
                setSelectedExportType(selectedOption.value as ExportType)
              );
            }
          }
        }}
        components={{ Option: IconOption, SingleValue: IconValue }}
      />
      {jobType !== "Invoice" &&
        jobType !== "Form" &&
        typeof selectedExportType === "string" &&
        (selectedExportType === "csv" ||
          selectedExportType?.startsWith("custom-")) && (
          <Select
            maxMenuHeight={600}
            className="select-export-type mt-2"
            options={secondSelectOptions}
            value={selectedCustomConfigCSV}
            onChange={(selectedOption) => {
              if (selectedOption) {
                if (selectedOption.value === "add-new") {
                  dispatch(toggleColumnConfigModal(true));
                  dispatch(
                    createCustomFormatRedux({
                      type: "bankStatement",
                    })
                  );
                  return;
                }
                onSelectedOption(selectedOption);
              }
            }}
            components={{ Option: IconOption, SingleValue: IconValue }}
          />
        )}
    </>
  );
};

export default ExportTypeDropdown;
